import { useCallback, useEffect, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { ChaseStats } from "@/components/communications/ChaseStats";
import { ChaseRow } from "@/components/communications/ChaseRow";
import { channelMeta } from "@/components/ChannelBadge";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const AutoChase = () => {
  const [data, setData] = useState(null);
  const [nudging, setNudging] = useState(null);

  const load = useCallback(() => {
    api.get("/communications/chases").then((r) => setData(r.data)).catch(() => toast.error("Could not load chases"));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("aria:acted", load);
    return () => window.removeEventListener("aria:acted", load);
  }, [load]);

  const nudge = async (chase) => {
    setNudging(chase.employee.id);
    try {
      const r = await api.post(`/communications/chases/${chase.employee.id}/nudge`);
      toast.success(`Nudged ${chase.employee.name.split(" ")[0]} on ${channelMeta(r.data.step.channel).label}`, {
        description: r.data.message.text,
      });
      load();
    } catch {
      toast.error("Nudge failed");
    } finally {
      setNudging(null);
    }
  };

  if (!data) {
    return (
      <div className="space-y-4" data-testid="autochase-loading">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-[14px] bg-[#141414]" />)}
        </div>
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-[14px] bg-[#141414]" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="autochase-page">
      <div>
        <h2 className="flex items-center gap-2.5 text-[15px] font-semibold tracking-tight">
          <span className="h-2 w-2 rounded-full bg-[#22C55E] pulse-dot" />
          Aria is chasing {data.stats.active_chases} {data.stats.active_chases === 1 ? "person" : "people"} right now — so you don't have to
        </h2>
        <p className="mt-0.5 text-[11.5px] text-[#A3A3A3]">
          Every missing receipt gets its own escalation playbook. Aria works Slack, Teams, email and text until it lands — and only pulls you in when the playbook runs out.
        </p>
      </div>

      <ChaseStats stats={data.stats} />

      <div className="space-y-2.5" data-testid="chase-board">
        {data.chases.length === 0 ? (
          <div className="flex flex-col items-center rounded-[14px] border border-[#222] bg-[#111111] px-6 py-14 text-center card-shadow">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[rgba(34,197,94,0.12)] text-[#4ADE80]">
              <CheckCircle2 size={18} />
            </span>
            <div className="mt-3 text-[13px] font-medium text-white">Nobody owes anything</div>
            <div className="mt-1 text-[11.5px] text-[#6b6b6b]">Every receipt is in. Aria will start chasing the moment one goes missing.</div>
          </div>
        ) : (
          data.chases.map((c) => <ChaseRow key={c.employee.id} chase={c} onNudge={nudge} nudging={nudging} />)
        )}
      </div>
    </div>
  );
};
